import { useState, useEffect, useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import { $currentUser } from '~/lib/stores/auth';
import { formatRelativeTime } from '~/lib/time';
import { apiEnableClaim, apiDisableClaim, apiGetClaimStatus, type ClaimStatus } from '~/lib/api/claims';

/** "in 6d" / "in 3h" — formatRelativeTime is past-only, expiry is a future date. */
function formatTimeUntil(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();

  if (ms <= 0) {
    return 'now';
  }

  const hours = Math.ceil(ms / 3_600_000);

  return hours >= 24 ? `in ${Math.round(hours / 24)}d` : `in ${hours}h`;
}

/*
 * Admin-only claim controls inside the share surface (DEV-169): a "claimable"
 * toggle that mints/revokes the claim link, a copy field for the link, and the
 * current state (unclaimed / claimed by whom / expired). Renders NOTHING for
 * non-admins — the role check is the first thing in the component, and every
 * endpoint it calls is AdminOnly server-side anyway.
 *
 * The raw claim URL only exists in the enable response (the server stores just
 * a hash), so it's shown once per enable; a re-open of the panel shows status
 * without the link and offers "New link" (rotate) instead.
 */
export function AdminClaimControls({ chatId }: { chatId: string }) {
  const user = useStore($currentUser);
  const isAdmin = user?.role === 'admin';

  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const [claimUrl, setClaimUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    if (!isAdmin) {
      return;
    }

    apiGetClaimStatus(chatId)
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [chatId, isAdmin]);

  useEffect(refresh, [refresh]);

  if (!isAdmin) {
    return null;
  }

  const enable = async () => {
    setBusy(true);

    try {
      const { url } = await apiEnableClaim(chatId);
      setClaimUrl(url);
      refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not create the claim link.');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);

    try {
      await apiDisableClaim(chatId);
      setClaimUrl(null);
      refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not revoke the claim link.');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!claimUrl) {
      return;
    }

    navigator.clipboard.writeText(claimUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const claimed = Boolean(status?.claimedBy);
  const active = Boolean(status?.claimable);

  return (
    <div className="-mx-3.5 mt-0.5 px-3.5 pt-3.5 border-t border-hairline">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="i-ph:hand-coins text-[14px] text-accent flex-shrink-0" />
          <span className="text-[12px] font-medium text-primary truncate">Hand off to a user</span>
          <span className="ui-pill text-muted flex-shrink-0">admin</span>
        </div>

        {!claimed && (
          <button
            type="button"
            onClick={active ? disable : enable}
            disabled={busy}
            className={classNames(
              'flex-shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer disabled:cursor-wait',
              active ? 'bg-danger/10 text-danger hover:bg-danger/15' : 'bg-accent/15 text-accent hover:bg-accent/25',
            )}
          >
            {busy ? '…' : active ? 'Revoke link' : 'Make claimable'}
          </button>
        )}
      </div>

      {claimed ? (
        <p className="text-[12px] text-muted leading-[1.55]">
          Claimed by <span className="text-secondary font-medium">{status?.claimedBy?.email}</span>
          {status?.claimedAt ? ` ${formatRelativeTime(status.claimedAt)}` : ''} — the generation now lives in their
          account.
        </p>
      ) : active && claimUrl ? (
        <div className="flex items-center gap-2 bg-sunken border border-hairline rounded-lg pl-3 pr-1.5 py-1.5">
          <span className="text-[12px] text-secondary truncate flex-1 text-left font-geist-mono">
            {claimUrl.replace(/^https?:\/\//, '')}
          </span>
          <button
            type="button"
            onClick={copy}
            className={classNames(
              'flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all',
              copied ? 'bg-accent/15 text-accent' : 'bg-raised text-secondary hover:bg-white/[0.06] hover:text-primary',
            )}
          >
            <div className={copied ? 'i-ph:check text-xs' : 'i-ph:copy text-xs'} />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      ) : active ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] text-muted leading-[1.55]">
            Link active{status?.expiresAt ? `, expires ${formatTimeUntil(status.expiresAt)}` : ''}. The URL shows once —
            rotate for a fresh one.
          </p>
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            className="flex-shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-raised text-secondary hover:text-primary hover:bg-white/[0.06] transition-all cursor-pointer disabled:cursor-wait"
          >
            New link
          </button>
        </div>
      ) : (
        <p className="text-[12px] text-muted leading-[1.55]">
          {status?.expired || status?.revoked
            ? 'The previous link is dead — make it claimable again to mint a new one.'
            : 'Generate a one-time link that transfers this generation — chat, code, and deployment — to whoever claims it.'}
        </p>
      )}
    </div>
  );
}
